import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { articles } from '@/data/articles';
import type { ArticleLocalized } from '@/data/articles';

const infoCards = [
  { titleKey: 'info_review_title', descKey: 'info_review_desc', icon: 'ri-star-line', tone: 'primary' },
  { titleKey: 'info_shipping_title', descKey: 'info_shipping_desc', icon: 'ri-ship-2-line', tone: 'accent' },
  { titleKey: 'info_legit_title', descKey: 'info_legit_desc', icon: 'ri-shield-check-line', tone: 'secondary' },
];

function resolveLocalized(article: { en: ArticleLocalized; pl: ArticleLocalized; es: ArticleLocalized; fr: ArticleLocalized }, lang: string): ArticleLocalized {
  const code = (lang || 'en').split('-')[0];
  if (code === 'pl') return article.pl;
  if (code === 'es') return article.es;
  if (code === 'fr') return article.fr;
  return article.en;
}

export default function ContentSections() {
  const { t, i18n } = useTranslation();

  return (
    <>
      {/* What is a USFans Spreadsheet */}
      <section id="what" className="scroll-mt-20">
        <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-16">
            <div>
              <h2 className="text-2xl font-bold text-foreground-950 md:text-3xl">{t('what_title')}</h2>
              <p className="mt-4 text-base leading-relaxed text-foreground-600">{t('what_body')}</p>
              <Link
                to="/article/usfans-spreadsheet-guide"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-secondary-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-secondary-600 cursor-pointer whitespace-nowrap"
              >
                {t('what_cta')}
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </span>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg border border-background-200 bg-white p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-layout-grid-line text-lg"></i>
                </span>
                <p className="mt-3 text-sm font-semibold text-foreground-900">{t('trust_1_title')}</p>
                <p className="mt-1 text-xs text-foreground-500">{t('trust_1_desc')}</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-white p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                  <i className="ri-time-line text-lg"></i>
                </span>
                <p className="mt-3 text-sm font-semibold text-foreground-900">{t('trust_2_title')}</p>
                <p className="mt-1 text-xs text-foreground-500">{t('trust_2_desc')}</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-white p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                  <i className="ri-refresh-line text-lg"></i>
                </span>
                <p className="mt-3 text-sm font-semibold text-foreground-900">{t('trust_3_title')}</p>
                <p className="mt-1 text-xs text-foreground-500">{t('trust_3_desc')}</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-white p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-shield-star-line text-lg"></i>
                </span>
                <p className="mt-3 text-sm font-semibold text-foreground-900">{t('trust_4_title')}</p>
                <p className="mt-1 text-xs text-foreground-500">{t('trust_4_desc')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillar articles */}
      <section id="guides" className="scroll-mt-20 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-foreground-950 md:text-3xl">{t('pillar_title')}</h2>
            <p className="mx-auto mt-3 max-w-2xl text-base text-foreground-600">{t('pillar_subtitle')}</p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {articles.map((article) => {
              const localized = resolveLocalized(article, i18n.language);
              return (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-white transition-transform hover:-translate-y-1 cursor-pointer"
                >
                  <div className="h-40 w-full overflow-hidden md:h-44">
                    <img
                      src={article.image}
                      alt={localized.title}
                      title={localized.title}
                      className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <span className="inline-flex w-fit items-center rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700 whitespace-nowrap">
                      {localized.category}
                    </span>
                    <h3 className="mt-3 text-base font-semibold leading-snug text-foreground-900 group-hover:text-primary-700">
                      {localized.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{localized.cardDesc}</p>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary-600 cursor-pointer">
                      {t('read_more')}
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className="ri-arrow-right-line text-sm"></i>
                      </span>
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Reviews / Shipping / Legit */}
      <section id="info" className="scroll-mt-20">
        <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {infoCards.map((card) => (
              <div key={card.titleKey} className="rounded-lg border border-background-200 bg-white p-6">
                <span
                  className={`flex h-12 w-12 items-center justify-center rounded-lg ${
                    card.tone === 'primary'
                      ? 'bg-primary-100 text-primary-600'
                      : card.tone === 'accent'
                        ? 'bg-accent-100 text-accent-600'
                        : 'bg-secondary-100 text-secondary-700'
                  }`}
                >
                  <i className={`text-2xl ${card.icon}`}></i>
                </span>
                <h3 className="mt-4 text-lg font-semibold text-foreground-900">{t(card.titleKey)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{t(card.descKey)}</p>
              </div>
            ))}
          </div>

          {/* Comparison table */}
          <div className="mt-12">
            <h3 className="text-xl font-bold text-foreground-950 md:text-2xl">{t('compare_title')}</h3>
            <p className="mt-2 max-w-2xl text-sm text-foreground-600">{t('compare_subtitle')}</p>
            <div className="mt-6 overflow-x-auto rounded-lg border border-background-200 bg-white">
              <table className="w-full min-w-[560px] text-left text-sm">
                <thead>
                  <tr className="border-b border-background-200 bg-background-100">
                    <th className="px-5 py-3 font-semibold text-foreground-900">{t('compare_col_feature')}</th>
                    <th className="px-5 py-3 font-semibold text-primary-700">{t('compare_col_spreadsheet')}</th>
                    <th className="px-5 py-3 font-semibold text-secondary-700">{t('compare_col_seller')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-background-100">
                    <td className="px-5 py-3 font-medium text-foreground-700">{t('compare_r1_label')}</td>
                    <td className="px-5 py-3 text-foreground-600">{t('compare_r1_a')}</td>
                    <td className="px-5 py-3 text-foreground-600">{t('compare_r1_b')}</td>
                  </tr>
                  <tr className="border-b border-background-100">
                    <td className="px-5 py-3 font-medium text-foreground-700">{t('compare_r2_label')}</td>
                    <td className="px-5 py-3 text-foreground-600">{t('compare_r2_a')}</td>
                    <td className="px-5 py-3 text-foreground-600">{t('compare_r2_b')}</td>
                  </tr>
                  <tr>
                    <td className="px-5 py-3 font-medium text-foreground-700">{t('compare_r3_label')}</td>
                    <td className="px-5 py-3 text-foreground-600">{t('compare_r3_a')}</td>
                    <td className="px-5 py-3 text-foreground-600">{t('compare_r3_b')}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Safety center */}
      <section id="safety" className="scroll-mt-20 bg-secondary-50">
        <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="flex flex-col items-start gap-6 rounded-lg border border-secondary-200 bg-white p-8 md:flex-row md:items-center md:justify-between md:p-10">
            <div className="max-w-2xl">
              <h2 className="text-2xl font-bold text-foreground-950 md:text-3xl">{t('safety_title')}</h2>
              <p className="mt-4 text-base leading-relaxed text-foreground-600">{t('safety_body')}</p>
            </div>
            <Link
              to="/article/is-usfans-legit"
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-secondary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-secondary-600 cursor-pointer whitespace-nowrap"
            >
              {t('safety_cta')}
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}